/**
 * Compliance Dashboard
 * 
 * Main compliance hub showing:
 * - Company profile
 * - Upcoming deadlines
 * - Compliance status
 * - Quick actions
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Settings, Calendar, FileText, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/apps/compliance/')({
  component: ComplianceDashboard,
});

function ComplianceDashboard() {
  // Fetch company profile
  const { data: companyProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userData?.company_id) throw new Error('No company found');

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', userData.company_id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Check if profile is configured
  const isProfileConfigured = companyProfile?.legal_form && companyProfile?.activity_type;

  if (isLoadingProfile) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-4 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Show setup prompt if not configured
  if (!isProfileConfigured) {
    return (
      <div className="container mx-auto py-12">
        <Card className="mx-auto max-w-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <AlertCircle className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Configuration requise</CardTitle>
            <CardDescription>
              Configurez votre profil juridique pour commencer à utiliser le module de conformité
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                🤖 Configuration intelligente
              </h4>
              <ul className="mt-2 space-y-1 text-xs text-blue-800 dark:text-blue-200">
                <li>✓ Upload de votre RC avec extraction automatique (OCR)</li>
                <li>✓ Détection automatique de vos obligations fiscales</li>
                <li>✓ Génération du calendrier de conformité</li>
                <li>✓ Rappels automatiques avant chaque échéance</li>
              </ul>
            </div>

            <div className="flex justify-center pt-4">
              <Link to="/apps/compliance/setup">
                <Button size="lg">
                  <Settings className="mr-2 h-5 w-5" />
                  Configurer maintenant
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show main dashboard
  return (
    <div className="container mx-auto space-y-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Centre de Conformité</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gérez vos obligations fiscales et légales
          </p>
        </div>
        <Link to="/apps/compliance/setup">
          <Button variant="outline">
            <Settings className="mr-2 h-4 w-4" />
            Modifier le profil
          </Button>
        </Link>
      </div>

      {/* Company Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Profil de l'entreprise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <p className="text-sm text-gray-600">Forme juridique</p>
              <p className="font-medium">{companyProfile.legal_form}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Type d'activité</p>
              <p className="font-medium">{companyProfile.activity_type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Régime fiscal</p>
              <p className="font-medium">{companyProfile.tax_regime}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Employés</p>
              <p className="font-medium">{companyProfile.employee_count || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon Sections */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Échéances à venir
            </CardTitle>
            <CardDescription>Vos prochaines obligations fiscales</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border-2 border-dashed p-8 text-center">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm font-medium">Calendrier en cours de développement</p>
              <p className="mt-1 text-xs text-gray-500">
                Disponible prochainement avec génération automatique
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Score de conformité
            </CardTitle>
            <CardDescription>Votre niveau de conformité fiscale</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border-2 border-dashed p-8 text-center">
              <div className="mx-auto h-24 w-24 rounded-full border-4 border-gray-300 bg-gray-100 dark:bg-gray-800">
                <div className="flex h-full items-center justify-center text-3xl font-bold text-gray-400">
                  ?
                </div>
              </div>
              <p className="mt-4 text-sm font-medium">Score en cours de calcul</p>
              <p className="mt-1 text-xs text-gray-500">
                Basé sur vos déclarations et paiements
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Link to="/apps/compliance/etat-104">
              <Button variant="outline" className="h-auto w-full flex-col gap-2 p-6">
                <FileText className="h-6 w-6 text-primary" />
                <span className="font-medium">Générer État 104</span>
                <span className="text-xs text-green-600">✓ Disponible maintenant!</span>
              </Button>
            </Link>
            <Button variant="outline" className="h-auto flex-col gap-2 p-6" disabled>
              <FileText className="h-6 w-6" />
              <span className="font-medium">Générer G50</span>
              <span className="text-xs text-gray-500">Disponible prochainement</span>
            </Button>
            <Button variant="outline" className="h-auto flex-col gap-2 p-6" disabled>
              <Calendar className="h-6 w-6" />
              <span className="font-medium">Voir Calendrier</span>
              <span className="text-xs text-gray-500">Disponible prochainement</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
